import { EmptyBox } from '@/components/EmptyBox';
import { api_portalOfficeHospitalListing_medicalSubject } from '@/init/swagger';
import { Box, Pagination } from '@mui/material';
import { HospitalListingItemForMultiRequest } from './HospitalListingItemForMultiRequest';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { CustomPalettes } from '@/themes';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useCallback,
} from 'react';

// リストの参照タイプ定義
export type HospitalListingListRef = {
  scrollToListing: (portalOfficeId: number, source?: 'click' | 'hover') => void;
};

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalListings: Array<{
    listing: HospitalListingOutput;
    checked: boolean;
    disabled: boolean;
  }>;
  multiRequestPaginationInfo: {
    lastPage: number;
    current: {
      page: number;
    };
  };
  onSelect: (listingInfo: HospitalListingOutput) => void;
  onChangePagination: (event: React.ChangeEvent<unknown>, page: number) => void;
  mapMarkerPortalOfficeId?: number | null;
  setMapMarkerPortalOfficeIdWithoutScroll?: (
    portalOfficeId: number | null,
  ) => void;
  hospitalListingListRef?: React.RefObject<HospitalListingListRef | null>;
  isShowMap?: boolean;
};

export const HospitalSearchResultsForMultiRequest = forwardRef<
  HospitalListingListRef,
  Props
>(function HospitalSearchResultsForMultiRequest(
  {
    hospitalMedicalSubjects,
    hospitalListings,
    multiRequestPaginationInfo,
    onSelect,
    onChangePagination,
    mapMarkerPortalOfficeId,
    setMapMarkerPortalOfficeIdWithoutScroll,
    hospitalListingListRef,
    isShowMap = false,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastSelectedIdRef = useRef<number | null>(null); // ちらつき防止用
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null); // デバウンス処理用
  const isMarkerClickScrollRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // スクロールコンテナを見つける関数
  const findScrollContainer = useCallback(() => {
    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent !== document.body) {
        const computedStyle = window.getComputedStyle(parent);
        const overflow = computedStyle.overflow || computedStyle.overflowY;

        if (overflow === 'auto' || overflow === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }
    }
    return null;
  }, []);

  const handleIntersection = useCallback(
    (_entries: IntersectionObserverEntry[]) => {
      if (!isShowMap || !setMapMarkerPortalOfficeIdWithoutScroll) {
        return;
      }

      // マーカークリックによるスクロール中は処理をスキップ
      if (isMarkerClickScrollRef.current) {
        return;
      }

      // デバウンス処理: 連続したイベントを制御
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        // スクロールコンテナを取得して、そのコンテナの中央を基準にする
        const scrollContainer = findScrollContainer();
        let containerCenter: number;

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          containerCenter = containerRect.top + containerRect.height / 2;
        } else {
          // フォールバック: ビューポートの中央
          containerCenter = window.innerHeight / 2;
        }

        // すべての表示中のアイテムから中央に最も近いものを選択
        const candidates: {
          id: number;
          centerDistance: number;
          intersectionRatio: number;
        }[] = [];

        // containerRef内のすべての病院要素をチェック
        if (containerRef.current) {
          const hospitalElements =
            containerRef.current.querySelectorAll('[data-hospital-id]');

          hospitalElements.forEach((element) => {
            const id = parseInt(
              element.getAttribute('data-hospital-id') || '0',
              10,
            );
            if (!id) return;

            const rect = element.getBoundingClientRect();
            const elementCenter = rect.top + rect.height / 2;

            // スクロールコンテナ内に表示されているかチェック
            const containerRect = scrollContainer
              ? scrollContainer.getBoundingClientRect()
              : { top: 0, bottom: window.innerHeight };

            const isVisible =
              rect.bottom > containerRect.top &&
              rect.top < containerRect.bottom;

            // 最低20%が表示されているかチェック
            const visibleHeight =
              Math.min(rect.bottom, containerRect.bottom) -
              Math.max(rect.top, containerRect.top);
            const intersectionRatio = Math.max(0, visibleHeight / rect.height);

            if (isVisible && intersectionRatio > 0.2) {
              const centerDistance = Math.abs(containerCenter - elementCenter);

              candidates.push({
                id,
                centerDistance,
                intersectionRatio,
              });
            }
          });
        }

        if (candidates.length === 0) {
          return;
        }

        // 一番上と一番下のアイテムをチェック
        const firstHospitalId = hospitalListings?.[0]?.listing.portal_office_id;
        const lastHospitalId =
          hospitalListings?.[hospitalListings.length - 1]?.listing
            .portal_office_id;

        // 表示されている候補の中から一番上・一番下を探す
        const firstCandidate = candidates.find((c) => c.id === firstHospitalId);
        const lastCandidate = candidates.find((c) => c.id === lastHospitalId);

        let selectedCandidate = null;

        // 一番上のアイテムが30%以上表示されている場合は優先選択
        if (firstCandidate && firstCandidate.intersectionRatio > 0.3) {
          selectedCandidate = firstCandidate;
        }
        // 一番下のアイテムが30%以上表示されている場合は優先選択（上が選択されていない場合のみ）
        else if (lastCandidate && lastCandidate.intersectionRatio > 0.3) {
          selectedCandidate = lastCandidate;
        }
        // 端のアイテムが十分に表示されていない場合は、中央選択ロジックを使用
        else {
          // 中央に最も近いアイテムを選択（表示率も考慮）
          selectedCandidate = candidates
            .filter((c) => c.intersectionRatio > 0.3) // より厳しい表示条件
            .sort((a, b) => {
              // 表示率の差が大きい場合は表示率を優先
              const ratioDiff = b.intersectionRatio - a.intersectionRatio;
              if (Math.abs(ratioDiff) > 0.2) {
                return ratioDiff;
              }
              // 表示率が近い場合は中央距離を優先
              return a.centerDistance - b.centerDistance;
            })[0];
        }

        // より厳格な条件で選択を変更
        if (
          selectedCandidate &&
          selectedCandidate.id !== lastSelectedIdRef.current
        ) {
          // 現在選択されているアイテムがまだ十分表示されているかチェック
          const currentCandidate = candidates.find(
            (c) => c.id === lastSelectedIdRef.current,
          );

          // 端のアイテムが選択される場合は、現在の選択状態に関係なく優先する
          const isEdgeSelection =
            selectedCandidate.id === firstHospitalId ||
            selectedCandidate.id === lastHospitalId;

          if (!isEdgeSelection) {
            // 中央選択の場合は従来の安定性チェックを適用
            // 現在のアイテムが40%以上表示されている場合は、新しい候補がより優位でない限り変更しない
            if (
              currentCandidate &&
              currentCandidate.intersectionRatio > 0.4 &&
              selectedCandidate.intersectionRatio -
                currentCandidate.intersectionRatio <
                0.3
            ) {
              return; // 選択を変更しない
            }
          }

          lastSelectedIdRef.current = selectedCandidate.id;
          setMapMarkerPortalOfficeIdWithoutScroll(selectedCandidate.id);
        }
      }, 200); // 安定性を重視して200msに調整
    },
    [
      isShowMap,
      setMapMarkerPortalOfficeIdWithoutScroll,
      findScrollContainer,
      hospitalListings,
    ],
  );

  // Intersection Observerの設定
  useEffect(() => {
    if (
      !isShowMap ||
      !setMapMarkerPortalOfficeIdWithoutScroll ||
      !containerRef.current
    ) {
      // 既存のobserverがある場合は停止
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // デバウンスタイマーもクリア
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    // スクロールコンテナを取得
    const scrollContainer = findScrollContainer();

    // Intersection Observerを作成（特定のスクロールコンテナ内での監視）
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: scrollContainer, // 特定のスクロールコンテナ内でのみ監視
      rootMargin: '0px',
      // より安定した閾値設定
      threshold: [0, 0.2, 0.4, 0.6, 0.8, 1.0],
    });

    // 現在の要素を監視対象に追加
    const elements =
      containerRef.current.querySelectorAll('[data-hospital-id]');
    elements.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // クリーンアップ時にタイマーもクリア
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [
    isShowMap,
    setMapMarkerPortalOfficeIdWithoutScroll,
    hospitalListings,
    handleIntersection,
    findScrollContainer,
  ]);

  useImperativeHandle(ref || hospitalListingListRef, () => ({
    scrollToListing: (portalOfficeId: number, source?: 'click' | 'hover') => {
      if (!containerRef.current) return;

      // マーカークリックによるスクロールの場合、一時的にObserver処理を無効化
      if (source === 'click') {
        isMarkerClickScrollRef.current = true;

        // マーカークリックされたアイテムのIDを強制的に設定
        lastSelectedIdRef.current = portalOfficeId;
        setMapMarkerPortalOfficeIdWithoutScroll?.(portalOfficeId);

        // スクロール完了を検知してから無効化を解除
        const onScrollEnd = () => {
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
          scrollTimeoutRef.current = setTimeout(() => {
            isMarkerClickScrollRef.current = false;
          }, 1000); // スクロール停止から1秒後に解除
        };

        // スクロール開始前に既存のタイマーをクリア
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // スクロール開始直後に一度呼び出す
        onScrollEnd();

        // 最大5秒後には必ず無効化を解除
        setTimeout(() => {
          isMarkerClickScrollRef.current = false;
        }, 5000);
      }

      const element = containerRef.current.querySelector(
        `[data-hospital-id="${portalOfficeId}"]`,
      );
      if (element) {
        element.scrollIntoView({
          behavior: source === 'click' ? 'smooth' : 'auto',
          block: 'center',
        });
      }
    },
  }));

  if (hospitalListings?.length < 1) {
    return (
      <Box sx={{ mx: 2 }}>
        <EmptyBox
          mainMessage="ご指定の条件に該当する医療機関が見つかりませんでした"
          subMessage="検索条件を変更して、再度検索してください。"
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          mt: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {hospitalListings?.map(({ listing, checked, disabled }) => (
          <Box
            key={listing.portal_office_id}
            id={`hospital-${listing.portal_office_id}`}
            data-hospital-id={listing.portal_office_id}
          >
            <HospitalListingItemForMultiRequest
              hospitalMedicalSubjects={hospitalMedicalSubjects}
              listingInfo={listing}
              disabled={disabled}
              checked={checked}
              onSelect={onSelect}
              mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
              isShowMap={isShowMap}
            />
          </Box>
        ))}
        <Pagination
          count={multiRequestPaginationInfo.lastPage}
          page={multiRequestPaginationInfo.current.page}
          onChange={onChangePagination}
          siblingCount={2}
          boundaryCount={0}
          variant="outlined"
          shape="rounded"
          showFirstButton
          showLastButton
          sx={{
            margin: '24px auto',
            '.MuiPaginationItem-root': {
              backgroundColor: CustomPalettes['Space/pallet/Gray/0'],
            },
            '.MuiPaginationItem-root.Mui-selected': {
              backgroundColor:
                CustomPalettes['Space/pallet/Primary/500-primary'],
              color: CustomPalettes['Space/pallet/Gray/0'],
            },
          }}
        />
      </Box>
    </>
  );
});
